import '../../../../../../node_modules/emoji-mart/css/emoji-mart.css'

import ComposeControls from 'components/ReusableComponents/Messaging/ComposeControls'
import {
  features,
  useUnlockService
} from 'components/ReusableComponents/UnlockServiceDialog'
import { Picker } from 'emoji-mart'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import TextareaAutosize from 'react-textarea-autosize'

import { postTyping } from '../../../../actions/messaging'
import { doesSupportEmoji, setCaretPosition } from '../../../../utils/helpers'
import styles from './Compose.module.scss'
import { ReactComponent as EmojiIcon } from './emoji.svg'
import { ReactComponent as SendIcon } from './send.svg'

const ComposeEmail = ({
  handleSubmit,
  chatId,
  disabled = false,
  closed = false,
  setMedium,
  currentEmail
}) => {
  const isInitialRender = useRef(true)
  const dispatch = useDispatch()

  let isDisabled = false

  if (disabled) {
    isDisabled = true
  }

  const [isTyping, setTyping] = useState(false)
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState('')
  const [subject, setSubject] = useState('')
  const [overwrittenEmail, setOverwrittenEmail] = useState(null)
  const [caretPos, setCaretPos] = useState(0)

  const [savedEmail, setSavedEmail] = useState(currentEmail)

  useEffect(() => {
    setSavedEmail(currentEmail)
  }, [currentEmail])

  useEffect(() => {
    if (value.trim().length > 0) {
      setTyping(true)
    } else {
      setTyping(false)
    }
  }, [value])

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
    } else {
      if (chatId) {
        dispatch(postTyping({ chatId, isTyping }))
      }
    }
  }, [chatId, dispatch, isTyping])

  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const emojiContainerRef = useRef()
  const inputRef = useRef()

  const { hasMessagingService, openUnlockServiceDialog } = useUnlockService()

  function sendMessage() {
    if (!hasMessagingService) {
      openUnlockServiceDialog(features.Messaging)
      return
    }

    handleSubmit(value, subject, overwrittenEmail)
    setValue('')
    setSubject('')
  }

  function handleFormSubmit(e) {
    e.preventDefault()

    if (disabled) return

    sendMessage()
  }

  useEffect(() => {
    inputRef.current.focus()
    setValue('')
    setSubject('')
    setSavedEmail(currentEmail)
    setOverwrittenEmail(null)
  }, [chatId, currentEmail])

  function handleClickEmojiButton(e) {
    e.preventDefault()
    setShowEmojiPicker(!showEmojiPicker)
    inputRef.current.focus()
    setFocused(true)
  }

  function handleSelectEmoji(emoji) {
    setShowEmojiPicker(false)
    setValue(
      [value.substring(0, caretPos), emoji.native, value.substring(caretPos)].join('')
    )

    setTimeout(() => {
      setCaretPosition(inputRef.current, caretPos + emoji.native.length)
    }, 10)
    setFocused(true)
  }

  function handleInputBlur(e) {
    setFocused(false)
    setCaretPos(e.target.selectionStart)
  }

  const handleClickOutsideEmojiButton = useCallback(e => {
    if (emojiContainerRef.current.contains(e.target)) {
      return
    }
    setShowEmojiPicker(false)
  }, [])

  useEffect(() => {
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutsideEmojiButton)
    } else {
      document.removeEventListener('mousedown', handleClickOutsideEmojiButton)
    }

    return () => document.removeEventListener('mousedown', handleClickOutsideEmojiButton)
  }, [handleClickOutsideEmojiButton, showEmojiPicker])

  function handleTextBoxKeyPress(e) {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault()
      handleFormSubmit(e)
    }
  }

  return (
    <>
      <div
        className={[styles.container, isDisabled ? styles.disabledContainer : ''].join(
          ' '
        )}
      >
        <div className={styles.formHolder}>
          <ComposeControls medium="email" setMedium={setMedium} />

          <form className={styles.form} onSubmit={handleFormSubmit}>
            <label className={styles.subjectLabel}>
              <span className={styles.subjectLabelInner}>To:</span>
              <input
                className={styles.subjectInput}
                type="email"
                required
                value={overwrittenEmail || savedEmail}
                onChange={e => setOverwrittenEmail(e.target.value)}
              />
            </label>
            <label className={styles.subjectLabel}>
              <span className={styles.subjectLabelInner}>Subject:</span>
              <input
                className={styles.subjectInput}
                type="text"
                required
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </label>
            <label
              className={[styles.label, focused ? styles.labelFocused : ''].join(' ')}
            >
              <TextareaAutosize
                onChange={e => setValue(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={handleInputBlur}
                placeholder={
                  closed ? 'Type a message to re-activate this' : 'Type a message...'
                }
                value={value}
                required
                ref={inputRef}
                disabled={isDisabled}
                maxLength={700}
                onKeyDown={handleTextBoxKeyPress}
              />
              <div ref={emojiContainerRef}>
                <button
                  type="button"
                  onClick={handleClickEmojiButton}
                  className={styles.buttonWithIcon}
                >
                  <EmojiIcon />
                </button>
                <EmojiPicker
                  show={showEmojiPicker}
                  onSelect={handleSelectEmoji}
                  className={styles.emojiPicker}
                  winClassName={styles.emojiPickerWin}
                />
              </div>
              <button className={styles.sendButton}>
                <SendIcon />
              </button>
            </label>
          </form>
        </div>
      </div>
    </>
  )
}

ComposeEmail.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  closed: PropTypes.bool,
  chatId: PropTypes.string
}

const EmojiPicker = ({ show, className, winClassName, onSelect, ref }) =>
  show ? (
    <div
      className={navigator.appVersion.indexOf('Win') !== -1 ? winClassName : className}
      ref={ref}
    >
      <Picker
        set="apple"
        onSelect={onSelect}
        showPreview={false}
        native={doesSupportEmoji()}
        showSkinTones={false}
        autoFocus={true}
      />
    </div>
  ) : null

export default ComposeEmail
